import React, { useState, useContext } from "react";
import Axios from "axios";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@material-ui/core";
import {
  PhoneAndroid,
  Delete,
  Close as CloseIconMUI,
  CheckCircle,
  Cancel,
} from "@material-ui/icons";
import AppContext from "../context/store";

const MobileSignatureDeviceButton = () => {
  const context = useContext(AppContext);

  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const fetchDevice = () => {
    setLoading(true);
    setData(null);
    Axios.get(`${context.api_endpoint}/esignature/mobile`)
      .then(({ data: res }) => {
        setData(res?.data || null);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.data ||
          "Mobil imza bilgileri alınamadı.";
        toast.error(msg);
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  const handleOpen = () => {
    setOpen(true);
    setDeleteConfirm(false);
    fetchDevice();
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteConfirm(false);
  };

  const handleDelete = () => {
    setDeleteLoading(true);
    Axios.delete(`${context.api_endpoint}/esignature/mobile`)
      .then(() => {
        toast.success("Mobil imza cihazı bağlantısı kaldırıldı.");
        setData(null);
        setDeleteConfirm(false);
        setOpen(false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.data ||
          "Cihaz kaldırılırken bir hata oluştu.";
        toast.error(msg);
      })
      .finally(() => setDeleteLoading(false));
  };

  return (
    <>
      <Trigger onClick={handleOpen}>
        <PhoneAndroid style={{ fontSize: 16 }} />
        Bağlı Mobil İmza Cihazı
      </Trigger>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle disableTypography>
          <TitleRow>
            <TitleContent>
              <PhoneAndroid style={{ fontSize: 21, color: "#1976d2" }} />
              Bağlı Mobil İmza Cihazı
            </TitleContent>
            <IconButton size="small" onClick={handleClose}>
              <CloseIconMUI fontSize="small" />
            </IconButton>
          </TitleRow>
        </DialogTitle>

        <DialogContent dividers style={{ padding: "16px 24px 24px" }}>
          {loading ? (
            <LoadingBox>
              <CircularProgress size={30} style={{ color: "#1976d2" }} />
              <span>Bilgiler yükleniyor...</span>
            </LoadingBox>
          ) : data ? (
            <>
              <StatusBadge active={!data.is_passive}>
                {!data.is_passive ? (
                  <>
                    <CheckCircle style={{ fontSize: 15 }} />
                    Aktif
                  </>
                ) : (
                  <>
                    <Cancel style={{ fontSize: 15 }} />
                    Pasif
                  </>
                )}
              </StatusBadge>

              <InfoGrid>
                <InfoRow>
                  <InfoLabel>Geçerlilik Başlangıcı</InfoLabel>
                  <InfoValue>{data.valid_from || "—"}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Geçerlilik Bitişi</InfoLabel>
                  <InfoValue>{data.valid_to || "—"}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Mobil İmza</InfoLabel>
                  <InfoValue>
                    {data.mobile_signature ? (
                      <span style={{ color: "#4caf50", fontWeight: 700 }}>Evet</span>
                    ) : (
                      <span style={{ color: "#f44336", fontWeight: 700 }}>Hayır</span>
                    )}
                  </InfoValue>
                </InfoRow>
              </InfoGrid>

              {!deleteConfirm ? (
                <DeleteBtn onClick={() => setDeleteConfirm(true)}>
                  <Delete style={{ fontSize: 17 }} />
                  Cihaz Bağlantısını Kaldır
                </DeleteBtn>
              ) : (
                <ConfirmBox>
                  <ConfirmText>
                    Bu cihazın bağlantısını kaldırmak istediğinize emin misiniz?
                  </ConfirmText>
                  <ConfirmActions>
                    <ConfirmYes onClick={handleDelete} disabled={deleteLoading}>
                      {deleteLoading ? (
                        <CircularProgress size={13} style={{ color: "#fff" }} />
                      ) : (
                        "Evet, Kaldır"
                      )}
                    </ConfirmYes>
                    <ConfirmNo
                      onClick={() => setDeleteConfirm(false)}
                      disabled={deleteLoading}
                    >
                      İptal
                    </ConfirmNo>
                  </ConfirmActions>
                </ConfirmBox>
              )}
            </>
          ) : (
            <EmptyBox>
              <PhoneAndroid style={{ fontSize: 38, color: "#bdbdbd" }} />
              <EmptyText>Bağlı mobil imza cihazı bulunamadı.</EmptyText>
            </EmptyBox>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MobileSignatureDeviceButton;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 6px;
  border: 1.5px solid #90caf9;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;

  &:hover {
    background: #bbdefb;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
`;

const LoadingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 30px 0;
  color: #777;
  font-size: 13px;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 14px;
  background: ${({ active }) => (active ? "#e8f5e9" : "#fce4ec")};
  color: ${({ active }) => (active ? "#2e7d32" : "#c62828")};
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-bottom: 18px;
  background: #f9f9fb;
  border-radius: 9px;
  padding: 13px 15px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  color: #888;
  font-weight: 500;
  white-space: nowrap;
`;

const InfoValue = styled.span`
  font-size: 13px;
  color: #222;
  font-weight: 600;
  text-align: right;
  word-break: break-all;
`;

const DeleteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 9px 0;
  border-radius: 8px;
  border: 1.5px solid #f44336;
  background: #fff5f5;
  color: #d32f2f;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #ffebee;
  }
`;

const ConfirmBox = styled.div`
  background: #fff8e1;
  border: 1.5px solid #ffca28;
  border-radius: 9px;
  padding: 13px 15px;
`;

const ConfirmText = styled.p`
  font-size: 13px;
  color: #5d4037;
  margin: 0 0 11px 0;
  font-weight: 500;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ConfirmYes = styled.button`
  flex: 1;
  padding: 8px 0;
  border-radius: 7px;
  border: none;
  background: #d32f2f;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.88;
  }
`;

const ConfirmNo = styled.button`
  flex: 1;
  padding: 8px 0;
  border-radius: 7px;
  border: 1.5px solid #bdbdbd;
  background: #fff;
  color: #555;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 30px 0;
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: #9e9e9e;
  margin: 0;
  text-align: center;
`;
